import React, { useCallback, useRef, useState, useEffect, memo, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Panel,
  ReactFlowProvider,
  addEdge,
  updateEdge,
  Connection,
  MarkerType,
  useKeyPress,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FlowchartData, ExportFormat } from '../types';
import { exportFlowchart } from '../utils/exportUtils';

// Prevent ResizeObserver loop errors
const fixResizeObserverLoopError = () => {
  // Detect ResizeObserver loop limit exceeded error
  const errorHandler = (e: ErrorEvent) => {
    if (e.message.includes('ResizeObserver loop') || e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      // Prevent the error from being displayed in console
      e.stopImmediatePropagation();
    }
  };

  window.addEventListener('error', errorHandler);
  
  return () => {
    window.removeEventListener('error', errorHandler);
  };
};

// Add custom CSS to reduce animation flashing
const addStabilityStyles = () => {
  // Create a style element if it doesn't exist
  if (!document.getElementById('reactflow-stability-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'reactflow-stability-styles';
    styleElement.innerHTML = `
      .react-flow__node {
        transition: transform 150ms ease-out, opacity 150ms ease-out !important;
        animation-duration: 150ms !important;
      }
      .react-flow__edge {
        transition: opacity 150ms ease-out !important;
        animation-duration: 150ms !important;
      }
      .react-flow__viewport {
        transition: transform 150ms ease-out !important;
      }
    `;
    document.head.appendChild(styleElement);
  }
};

// Node type definitions for custom nodes
const NODE_TYPES = {
  process: 'process',
  decision: 'decision', 
  start: 'start',
  end: 'end',
  cloud: 'cloud'
};

// Utility function to capitalize first letter
const capitalizeFirstLetter = (text: string) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

interface FlowchartCanvasProps {
  flowchartData: FlowchartData;
  onFlowchartDataChange: (data: FlowchartData) => void;
}

// Custom node types
const nodeTypes = {};

// Create a memoized version of the FlowchartCanvas component
const FlowchartCanvas: React.FC<FlowchartCanvasProps> = memo(({ 
  flowchartData, 
  onFlowchartDataChange 
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(flowchartData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowchartData.edges);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<any>(null);
  const edgeUpdateSuccessful = useRef(true);
  const [selectedElements, setSelectedElements] = useState<{ nodes: Node[]; edges: Edge[] }>({ nodes: [], edges: [] });
  const [history, setHistory] = useState<Array<{ nodes: Node[]; edges: Edge[] }>>([{ nodes: flowchartData.nodes, edges: flowchartData.edges }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showShapeSelector, setShowShapeSelector] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  
  // Add state for editing node label
  const [editingNode, setEditingNode] = useState<{id: string, label: string} | null>(null);
  
  // Previous flowchart data for comparison
  const prevFlowchartDataRef = useRef<FlowchartData | null>(null);
  
  // Keyboard controls
  const deleteKeyPressed = useKeyPress(['Delete', 'Backspace']);
  const escapeKeyPressed = useKeyPress('Escape');
  const undoKeyPressed = useKeyPress(['z']);
  const isCtrlPressed = useKeyPress(['Control', 'Meta']); // Meta for Mac
  
  // Apply stability styles on component mount
  useEffect(() => {
    addStabilityStyles();
  }, []);
  
  // Memoize ReactFlow options to prevent re-renders
  const reactFlowOptions = useMemo(() => ({
    defaultViewport: { x: 0, y: 0, zoom: 1 },
    minZoom: 0.2,
    maxZoom: 2,
    elementsSelectable: true,
    nodesDraggable: true,
    nodesConnectable: true,
    zoomOnScroll: true,
    zoomOnPinch: true,
    panOnScroll: false,
    panOnDrag: true,
    preventScrolling: true,
    snapToGrid: true,
    snapGrid: [15, 15] as [number, number],
    fitView: true,
    attributionPosition: 'bottom-left' as const,
  }), []);
  
  // Track selected elements
  const onSelectionChange = useCallback((elements: { nodes: Node[]; edges: Edge[] }) => {
    setSelectedElements(elements);
    
    // Set selectedNodeId if exactly one node is selected
    if (elements.nodes.length === 1) {
      setSelectedNodeId(elements.nodes[0].id);
    } else {
      setSelectedNodeId(null);
    }
  }, []);
  
  // Keep track of changes for undo history
  const saveToHistory = useCallback((newNodes: Node[], newEdges: Edge[]) => {
    const newHistoryEntry = { nodes: [...newNodes], edges: [...newEdges] };
    
    // Only save history if there's an actual change
    const lastEntry = history[historyIndex];
    const nodesChanged = JSON.stringify(lastEntry.nodes) !== JSON.stringify(newNodes);
    const edgesChanged = JSON.stringify(lastEntry.edges) !== JSON.stringify(newEdges);
    
    if (nodesChanged || edgesChanged) {
      // Truncate forward history if we're not at the latest point
      const newHistory = history.slice(0, historyIndex + 1);
      setHistory([...newHistory, newHistoryEntry]);
      setHistoryIndex(newHistory.length);
    }
  }, [history, historyIndex]);
  
  // Debounced update function for history
  useEffect(() => {
    const timer = setTimeout(() => {
      saveToHistory(nodes, edges);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [nodes, edges, saveToHistory]);
  
  // Update nodes and edges when flowchartData changes, with stability checks
  React.useEffect(() => {
    // Skip processing if the data hasn't meaningfully changed
    if (prevFlowchartDataRef.current && 
        nodes.length === flowchartData.nodes.length &&
        edges.length === flowchartData.edges.length &&
        JSON.stringify(prevFlowchartDataRef.current) === JSON.stringify(flowchartData)) {
      return;
    }
    
    prevFlowchartDataRef.current = flowchartData;
    
    // Capitalize all node labels before setting them
    const capitalizedNodes = flowchartData.nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        label: capitalizeFirstLetter(node.data.label)
      }
    }));
    
    setNodes(capitalizedNodes);
    setEdges(flowchartData.edges);
    setHistory([{ nodes: capitalizedNodes, edges: flowchartData.edges }]);
    setHistoryIndex(0);
    
    // Use a timeout to allow the DOM to settle before fitting the view
    if (reactFlowInstance.current) {
      setTimeout(() => {
        reactFlowInstance.current.fitView({ padding: 0.2, duration: 200 });
      }, 50);
    }
  }, [flowchartData, setNodes, setEdges]);

  // Update parent when internal state changes
  React.useEffect(() => {
    onFlowchartDataChange({ nodes, edges });
  }, [nodes, edges, onFlowchartDataChange]);

  // Handle key presses for operations
  useEffect(() => {
    if (deleteKeyPressed && (selectedElements.nodes.length > 0 || selectedElements.edges.length > 0)) {
      setNodes(nds => nds.filter(node => !selectedElements.nodes.find(n => n.id === node.id)));
      setEdges(eds => eds.filter(edge => !selectedElements.edges.find(e => e.id === edge.id)));
    }
  }, [deleteKeyPressed, selectedElements, setNodes, setEdges]);
  
  useEffect(() => {
    if (escapeKeyPressed) {
      setSelectedElements({ nodes: [], edges: [] });
      setShowShapeSelector(false);
    }
  }, [escapeKeyPressed]);
  
  // Handle undo operation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Z or Cmd+Z (macOS)
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        if (historyIndex > 0) {
          const prevIndex = historyIndex - 1;
          const prevState = history[prevIndex];
          setNodes(prevState.nodes);
          setEdges(prevState.edges);
          setHistoryIndex(prevIndex);
        }
      }
      
      // Check for Ctrl+Y or Cmd+Shift+Z (macOS) for redo
      if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.shiftKey && event.key === 'z'))) {
        event.preventDefault();
        if (historyIndex < history.length - 1) {
          const nextIndex = historyIndex + 1;
          const nextState = history[nextIndex];
          setNodes(nextState.nodes);
          setEdges(nextState.edges);
          setHistoryIndex(nextIndex);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [history, historyIndex, setNodes, setEdges]);

  const onInit = useCallback((instance: any) => {
    reactFlowInstance.current = instance;
    
    // Center view after initialization
    setTimeout(() => {
      instance.fitView({ padding: 0.2 });
    }, 100);
  }, []);

  // Handle new connections when creating edges
  const onConnect = useCallback((connection: Connection) => {
    // Create a new edge with a default style
    const newEdge = {
      ...connection,
      id: `edge_${Date.now()}`,
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#555' },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#555',
      },
    };
    
    setEdges(eds => addEdge(newEdge, eds));
  }, [setEdges]);
  
  // Handle edge updates (when an edge's connection is changed)
  const onEdgeUpdate = useCallback((oldEdge: Edge, newConnection: Connection) => {
    edgeUpdateSuccessful.current = true;
    setEdges(eds => updateEdge(oldEdge, newConnection, eds));
  }, [setEdges]);
  
  // Handle right-click for context menu
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    setSelectedNodeId(node.id);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
    setShowShapeSelector(true);
  }, []);
  
  // Handle canvas click to close context menu
  const onPaneClick = useCallback(() => {
    setShowShapeSelector(false);
  }, []);
  
  // Add a new node when double clicking on the canvas
  const onDoubleClick = useCallback((event: React.MouseEvent) => {
    if (!reactFlowInstance.current) return;
    
    // Get the position where we clicked
    const position = reactFlowInstance.current.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    
    // Create a new node
    const newNode: Node = {
      id: `node_${Date.now()}`,
      type: 'default',
      position,
      data: { label: 'New Node' },
      style: { 
        background: '#e9ecef', 
        border: '1px solid #adb5bd',
        borderRadius: '4px',
        width: 200,
        padding: 10
      }
    };
    
    setNodes(nds => [...nds, newNode]);
  }, [setNodes]);
  
  // Handle node double click for text editing
  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    setEditingNode({ id: node.id, label: node.data.label });
  }, []);
  
  // Handle editing completion
  const handleNodeLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (editingNode) {
      setEditingNode({...editingNode, label: e.target.value});
    }
  }, [editingNode]);
  
  const handleNodeLabelSubmit = useCallback(() => {
    if (editingNode && editingNode.label.trim()) {
      const capitalizedLabel = capitalizeFirstLetter(editingNode.label.trim());
      
      setNodes(nds => 
        nds.map(n => {
          if (n.id === editingNode.id) {
            return {
              ...n,
              data: {
                ...n.data,
                label: capitalizedLabel,
              },
            };
          }
          return n;
        })
      );
    }
    setEditingNode(null);
  }, [editingNode, setNodes]);
  
  // Handle Enter key and Escape key
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNodeLabelSubmit();
    }
    if (e.key === 'Escape') {
      setEditingNode(null);
    }
    e.stopPropagation();
  }, [handleNodeLabelSubmit]);
  
  // Change node shape
  const changeNodeShape = useCallback((nodeId: string, shapeType: string) => {
    setNodes(nds => 
      nds.map(node => {
        if (node.id === nodeId) {
          let style = { ...node.style };
          let nodeType = node.type;
          
          switch(shapeType) {
            case 'rectangle':
              style = { 
                background: '#e9ecef', 
                border: '1px solid #adb5bd',
                borderRadius: '4px',
                width: 200,
                padding: 10
              };
              nodeType = 'default';
              break;
            case 'diamond':
              style = { 
                background: '#fbe8a6', 
                border: '1px solid #f4a261',
                borderRadius: '4px',
                width: 150,
                height: 150,
                padding: 10,
                transform: 'rotate(45deg)',
                transformOrigin: 'center',
              };
              nodeType = 'default';
              break;
            case 'circle':
              style = { 
                background: '#d4f0f0', 
                border: '1px solid #2a9d8f',
                borderRadius: '50%',
                width: 150,
                height: 150,
                padding: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              };
              nodeType = 'default';
              break;
            case 'cloud':
              style = { 
                background: '#f1f3f5',
                border: '1px solid #adb5bd',
                borderRadius: '24px',
                width: 200,
                padding: 15,
              };
              nodeType = 'default';
              break;
            case 'start':
              style = { 
                background: '#d4f0f0', 
                border: '1px solid #2a9d8f',
                borderRadius: '30px',
                width: 150,
                padding: 10
              };
              nodeType = 'input';
              break;
            case 'end':
              style = { 
                background: '#ffcfd2',
                border: '1px solid #e76f51',
                borderRadius: '30px',
                width: 150,
                padding: 10
              };
              nodeType = 'output';
              break;
          }
          
          // Special handling for diamond shape to position the text correctly
          const data = shapeType === 'diamond' 
            ? { ...node.data, style: { transform: 'rotate(-45deg)' } }
            : node.data;
            
          return {
            ...node,
            type: nodeType,
            style,
            data
          };
        }
        return node;
      })
    );
    
    setShowShapeSelector(false);
  }, [setNodes]);
  
  // Change node color
  const changeNodeColor = useCallback((nodeId: string, colorScheme: string) => {
    setNodes(nds => 
      nds.map(node => {
        if (node.id === nodeId) {
          let style = { ...node.style };
          
          switch(colorScheme) {
            case 'blue':
              style = { 
                ...style,
                background: '#cfe2ff', 
                border: '1px solid #0d6efd',
              };
              break;
            case 'green':
              style = { 
                ...style,
                background: '#d1e7dd', 
                border: '1px solid #198754',
              };
              break;
            case 'yellow':
              style = { 
                ...style,
                background: '#fff3cd', 
                border: '1px solid #ffc107',
              };
              break;
            case 'red':
              style = { 
                ...style,
                background: '#f8d7da', 
                border: '1px solid #dc3545',
              };
              break;
            case 'purple':
              style = { 
                ...style,
                background: '#e2d9f3', 
                border: '1px solid #6f42c1',
              };
              break;
            case 'gray':
              style = { 
                ...style,
                background: '#e9ecef', 
                border: '1px solid #adb5bd',
              };
              break;
          }
          
          return {
            ...node,
            style
          };
        }
        return node;
      })
    );
  }, [setNodes]);
  
  // Change edge style
  const changeEdgeStyle = useCallback((edgeId: string, style: string) => {
    setEdges(eds => 
      eds.map(edge => {
        if (edge.id === edgeId) {
          let edgeStyle = { ...edge.style };
          let animated = edge.animated;
          let type = edge.type;
          
          switch(style) {
            case 'solid':
              edgeStyle = { 
                ...edgeStyle,
                stroke: '#555', 
                strokeWidth: 2,
                strokeDasharray: '' 
              };
              animated = false;
              type = 'smoothstep';
              break;
            case 'dashed':
              edgeStyle = { 
                ...edgeStyle,
                stroke: '#555', 
                strokeWidth: 2,
                strokeDasharray: '5,5' 
              };
              animated = false;
              type = 'smoothstep';
              break;
            case 'dotted':
              edgeStyle = { 
                ...edgeStyle,
                stroke: '#555', 
                strokeWidth: 2,
                strokeDasharray: '2,2' 
              };
              animated = false;
              type = 'smoothstep';
              break;
            case 'animated':
              edgeStyle = { 
                ...edgeStyle,
                stroke: '#ff0072', 
                strokeWidth: 2
              };
              animated = true;
              type = 'smoothstep';
              break;
            case 'straight':
              type = 'default';
              break;
            case 'step':
              type = 'step';
              break;
          }
          
          return {
            ...edge,
            animated,
            type,
            style: edgeStyle
          };
        }
        return edge;
      })
    );
  }, [setEdges]);
  
  const handleExport = async (format: ExportFormat) => {
    if (reactFlowWrapper.current) {
      try {
        await exportFlowchart(
          reactFlowWrapper.current.querySelector('.react-flow') as HTMLElement,
          format
        );
      } catch (error) {
        console.error('Error exporting flowchart:', error);
      }
    }
  };

  return (
    <div className="flowchart-canvas-container" ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onInit={onInit}
        onConnect={onConnect}
        onEdgeUpdate={onEdgeUpdate}
        onDoubleClick={onDoubleClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeContextMenu={onNodeContextMenu}
        onPaneClick={onPaneClick}
        onSelectionChange={onSelectionChange}
        deleteKeyCode={null} // Disable built-in delete to use our custom handler
        multiSelectionKeyCode={['Control', 'Meta']} // For multiselection
        selectionKeyCode={null} // Disable built-in selection to use our custom handler
        {...reactFlowOptions}
        nodeTypes={nodeTypes}
      >
        <Background color="#aaa" gap={16} />
        <Controls />
        <MiniMap nodeStrokeWidth={3} zoomable pannable />
        
        <Panel position="top-right" className="export-controls">
          <div className="export-buttons">
            <button onClick={() => handleExport('png')}>
              Export PNG
            </button>
            <button onClick={() => handleExport('svg')}>
              Export SVG
            </button>
            <button onClick={() => handleExport('pdf')}>
              Export PDF
            </button>
          </div>
        </Panel>
        
        <Panel position="top-left" className="tools-panel">
          <div className="flowchart-tools">
            <div className="toolbar-section">
              <h4>Shapes</h4>
              <div className="shape-buttons">
                <button 
                  className="shape-btn rectangle" 
                  title="Rectangle"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeShape(selectedNodeId, 'rectangle');
                    }
                  }}
                ></button>
                <button 
                  className="shape-btn diamond" 
                  title="Diamond"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeShape(selectedNodeId, 'diamond');
                    }
                  }}
                ></button>
                <button 
                  className="shape-btn circle" 
                  title="Circle"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeShape(selectedNodeId, 'circle');
                    }
                  }}
                ></button>
                <button 
                  className="shape-btn cloud" 
                  title="Cloud"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeShape(selectedNodeId, 'cloud');
                    }
                  }}
                ></button>
                <button 
                  className="shape-btn start-end" 
                  title="Start/End"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeShape(selectedNodeId, 'start');
                    }
                  }}
                ></button>
              </div>
            </div>
            
            <div className="toolbar-section">
              <h4>Colors</h4>
              <div className="color-buttons">
                <button 
                  className="color-btn blue" 
                  title="Blue"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeColor(selectedNodeId, 'blue');
                    }
                  }}
                ></button>
                <button 
                  className="color-btn green" 
                  title="Green"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeColor(selectedNodeId, 'green');
                    }
                  }}
                ></button>
                <button 
                  className="color-btn yellow" 
                  title="Yellow"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeColor(selectedNodeId, 'yellow');
                    }
                  }}
                ></button>
                <button 
                  className="color-btn red" 
                  title="Red"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeColor(selectedNodeId, 'red');
                    }
                  }}
                ></button>
                <button 
                  className="color-btn purple" 
                  title="Purple"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeColor(selectedNodeId, 'purple');
                    }
                  }}
                ></button>
                <button 
                  className="color-btn gray" 
                  title="Gray"
                  onClick={() => {
                    if (selectedNodeId) {
                      changeNodeColor(selectedNodeId, 'gray');
                    }
                  }}
                ></button>
              </div>
            </div>
            
            <div className="toolbar-section">
              <h4>Edge Styles</h4>
              <div className="edge-style-buttons">
                <button 
                  className="edge-btn solid" 
                  title="Solid"
                  onClick={() => {
                    if (selectedElements.edges.length) {
                      changeEdgeStyle(selectedElements.edges[0].id, 'solid');
                    }
                  }}
                ></button>
                <button 
                  className="edge-btn dashed" 
                  title="Dashed"
                  onClick={() => {
                    if (selectedElements.edges.length) {
                      changeEdgeStyle(selectedElements.edges[0].id, 'dashed');
                    }
                  }}
                ></button>
                <button 
                  className="edge-btn dotted" 
                  title="Dotted"
                  onClick={() => {
                    if (selectedElements.edges.length) {
                      changeEdgeStyle(selectedElements.edges[0].id, 'dotted');
                    }
                  }}
                ></button>
                <button 
                  className="edge-btn animated" 
                  title="Animated"
                  onClick={() => {
                    if (selectedElements.edges.length) {
                      changeEdgeStyle(selectedElements.edges[0].id, 'animated');
                    }
                  }}
                ></button>
              </div>
            </div>
          </div>
        </Panel>
        
        <Panel position="bottom-left" className="help-panel">
          <div className="keyboard-shortcuts">
            <h4>Keyboard Shortcuts</h4>
            <ul>
              <li><kbd>Double-click</kbd> canvas: Add new node</li>
              <li><kbd>Double-click</kbd> node: Edit text</li>
              <li><kbd>Right-click</kbd> node: Change shape (select first)</li>
              <li><kbd>Delete</kbd> / <kbd>Backspace</kbd>: Delete selected</li>
              <li><kbd>Ctrl+Z</kbd> / <kbd>⌘+Z</kbd>: Undo</li>
              <li><kbd>Ctrl+Y</kbd> / <kbd>⌘+Shift+Z</kbd>: Redo</li>
              <li><kbd>Escape</kbd>: Deselect all</li>
            </ul>
          </div>
        </Panel>
      </ReactFlow>
      
      {showShapeSelector && (
        <div 
          className="context-menu"
          style={{
            position: 'absolute',
            top: `${contextMenuPosition.y}px`,
            left: `${contextMenuPosition.x}px`,
          }}
        >
          <div className="context-menu-content">
            <h4>Change Shape</h4>
            <button onClick={() => changeNodeShape(selectedNodeId!, 'rectangle')}>Rectangle</button>
            <button onClick={() => changeNodeShape(selectedNodeId!, 'diamond')}>Diamond</button>
            <button onClick={() => changeNodeShape(selectedNodeId!, 'circle')}>Circle</button>
            <button onClick={() => changeNodeShape(selectedNodeId!, 'cloud')}>Cloud</button>
            <button onClick={() => changeNodeShape(selectedNodeId!, 'start')}>Start</button>
            <button onClick={() => changeNodeShape(selectedNodeId!, 'end')}>End</button>
          </div>
        </div>
      )}
      
      {editingNode && (
        <div 
          className="node-edit-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onClick={() => handleNodeLabelSubmit()}
        >
          <div 
            style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px'}}
            onClick={(e) => e.stopPropagation()}
          >
            <h4>Edit Node Text</h4>
            <input
              type="text"
              value={editingNode.label}
              onChange={handleNodeLabelChange}
              onKeyDown={handleKeyDown}
              autoFocus
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '10px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
              <button 
                onClick={() => setEditingNode(null)}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#f1f3f5',
                  border: '1px solid #adb5bd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleNodeLabelSubmit}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#0d6efd',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// Wrap component with ReactFlowProvider to ensure proper context is available
export default function FlowchartCanvasWrapper(props: FlowchartCanvasProps) {
  // Apply the ResizeObserver error fix
  useEffect(() => {
    const cleanup = fixResizeObserverLoopError();
    return cleanup;
  }, []);

  return (
    <ReactFlowProvider>
      <FlowchartCanvas {...props} />
    </ReactFlowProvider>
  );
} 